import { faInstagram, faTiktok, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import React from 'react';
import { useParams } from 'react-router-dom';
import ContentContainer from '~/src/components/global/ContentContainer';
import Layout from '~/src/components/global/Layout/Index';
import FightCard from '~/src/components/shared/FightCard';
import PhotoVideo from '~/src/components/shared/PhotoVideo';
import SectionHeading from '~/src/components/shared/SectionHeading';
import Urls from '~/src/constants/urls';
import {
	imgIconChampionBadgePng,
	imgIconTrophySvg,
	imgOtxBrandmarkBlackPng,
	imgPlaceholdersFighterFemaleBluePng,
	imgPlaceholdersFighterMaleBluePng,
} from '~/src/images';
import { OtxFight } from '~/src/models/OtxFight';
import { Genders, GetAge, GetFeetInches, GetFullName, OtxFighter } from '~/src/models/OtxFighter';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import { RoutePaths } from '~/src/www/routes';
import CountryFlag from '../shared/CountryFlag';
import './Fighter.scss';
import NotFound from './NotFound';

interface Statistic {
	name: string;
	value: React.ReactNode;
}

const _Statistics = ({ fighter }: { fighter: OtxFighter }) => {
	const statistics: Statistic[] = [
		{ name: 'Height', value: GetFeetInches(fighter.height) },
		{ name: 'Reach', value: fighter.reach ? `${fighter.reach}"` : null },
		{ name: 'Age', value: GetAge(fighter) },
		{ name: 'Weight', value: fighter.weight ? fighter.weight : null },
		{ name: 'Stance', value: fighter.stance },
		{
			name: 'Hometown',
			value: (
				<>
					{fighter.hometown}
					<CountryFlag countryCode={fighter.country} />
				</>
			),
		},
	];

	return (
		<>
			{statistics.map((s) => (
				<div className="item" key={s.name}>
					<div className="key">{s.name}</div>
					<div className={`value${_.isNil(s.value) ? ' placeholder' : ''}`}>{_.isNil(s.value) ? '–' : s.value}</div>
				</div>
			))}
		</>
	);
};

const Fighter = () => {
	const { otx_fighter_id } = useParams();

	const tournamentFights = useFetch<OtxFight[]>({
		url: Urls.ApiTournamentFights,
		key: 'otx_fights',
	}).result.read();

	const fighter = useFetch<OtxFighter>({
		url: `${Urls.ApiFighters}/${otx_fighter_id}`,
		key: 'otx_fighter',
	})?.result.read();

	if (!fighter) {
		return <NotFound />;
	}

	// Hydrate fights with fight details including fighters
	fighter.otx_fights = [];
	for (const fight of [...fighter.otx_fights1, ...fighter.otx_fights2]) {
		const getOtxFight = (id: string) => {
			return useFetch<OtxFight>({ url: `${Urls.ApiFights}/${id}`, key: 'otx_fight' })?.result.read();
		};

		// Note: Keep an eye on this if we notice any bugs with fights not showing up correctly
		// There are possible issues of using useFetch this way -- if we run into issues, we should refactor this
		fighter.otx_fights.push(getOtxFight(fight.id));
	}

	const fights = _.orderBy(
		fighter.otx_fights?.filter((f) => f.otx_fighter1 && f.otx_fighter2),
		(f) => f.starts_at,
		'desc',
	);

	const rankings = [
		{ organization: 'IBF', ranking: fighter.ranking_ibf },
		{ organization: 'WBA', ranking: fighter.ranking_wba },
		{ organization: 'WBC', ranking: fighter.ranking_wbc },
		{ organization: 'WBO', ranking: fighter.ranking_wbo },
	];

	const organizationRankings = _.orderBy(
		rankings.filter((r) => r.ranking > 0),
		['ranking', 'organization'],
		['asc', 'asc'],
	);

	return (
		<Layout title={GetFullName(fighter)} className="fighter-page">
			<section className="fighter-container">
				<ContentContainer>
					<section className="fighter">
						<section className="hero">
							<section className="hero-container">
								<div className="hero-image-container">
									<div className="hero-image">
										{fighter.image_path ? (
											<PhotoVideo src={`${Urls.OvertimeImagesOtx}/${fighter.image_path}`} mobileWidth={285} />
										) : fighter.gender === Genders.Female ? (
											<PhotoVideo src={`${imgPlaceholdersFighterFemaleBluePng}`} mobileWidth={285} />
										) : (
											<PhotoVideo src={`${imgPlaceholdersFighterMaleBluePng}`} mobileWidth={285} />
										)}
									</div>
									<div className="hero-image-background">
										<img src={imgOtxBrandmarkBlackPng} />
									</div>
								</div>
								{organizationRankings.length > 0 ? (
									<div className="organization-rankings">
										{organizationRankings.map((r) => (
											<div key={r.organization} className="organization">
												<div className="organization-ranking">
													<span className="ranking">#{r.ranking}</span> {r.organization}
												</div>
											</div>
										))}
									</div>
								) : null}
							</section>
							<div className="name">
								{fighter.nick_name && <div className="nick-name">"{fighter.nick_name}"</div>}
								<div className="full-name">
									<div className="first-name">{fighter.first_name}</div>
									<div className="last-name">{fighter.last_name}</div>
								</div>
								<a className="weight-class" href={`${RoutePaths.Fighters}#${fighter.weight_class}`}>
									{fighter.weight_class}
								</a>
								<div className="social desktop-only">
									{fighter.social_tiktok && (
										<a href={fighter.social_tiktok}>
											<FontAwesomeIcon icon={faTiktok} />
										</a>
									)}
									{fighter.social_instagram && (
										<a href={fighter.social_instagram}>
											<FontAwesomeIcon icon={faInstagram} />
										</a>
									)}
									{fighter.social_youtube && (
										<a href={fighter.social_youtube}>
											<FontAwesomeIcon icon={faYoutube} />
										</a>
									)}
									{fighter.social_twitter && (
										<a href={fighter.social_twitter}>
											<FontAwesomeIcon icon={faXTwitter} />
										</a>
									)}
								</div>
							</div>
						</section>
						<section className="record">
							{organizationRankings.length > 0 ? (
								<div className="organization-rankings">
									{organizationRankings.map((r) => (
										<div key={r.organization} className="organization">
											<span className="ranking">#{r.ranking}</span> {r.organization}
										</div>
									))}
								</div>
							) : null}

							<div className="record-cards">
								<div className="record-card">
									<h2 className="value">{fighter.wins ?? 0}</h2>
									<div className="label">Win</div>
								</div>
								<div className="record-card">
									<h2 className="value">{fighter.loses ?? 0}</h2>
									<div className="label">Loss</div>
								</div>
								<div className="record-card">
									<h2 className="value">{fighter.split_decisions ?? 0}</h2>
									<div className="label">Draw</div>
								</div>
							</div>
							{(fighter.kos > 0 || fighter.tkos > 0) && (
								<div className="knock-outs">
									{fighter.kos > 0 && <div className="knock-out">KOs: {fighter.kos ?? 0}</div>}
									{fighter.tkos > 0 && <div className="knock-out">TKOs: {fighter.tkos ?? 0}</div>}
								</div>
							)}
						</section>
					</section>
				</ContentContainer>
			</section>
			<section className="statistics-container">
				<ContentContainer>
					<section className="statistics">
						<div className="personal">
							<_Statistics fighter={fighter} />
						</div>
						{fighter.biography ||
						fighter.social_tiktok ||
						fighter.social_instagram ||
						fighter.social_youtube ||
						fighter.social_twitter ? (
							<section className="biography">
								{fighter.biography?.split('\n\n').map((b, i) => <p key={i}>{b}</p>)}
								<div className="social mobile-only">
									{fighter.social_tiktok && (
										<a href={fighter.social_tiktok}>
											<FontAwesomeIcon icon={faTiktok} />
										</a>
									)}
									{fighter.social_instagram && (
										<a href={fighter.social_instagram}>
											<FontAwesomeIcon icon={faInstagram} />
										</a>
									)}
									{fighter.social_youtube && (
										<a href={fighter.social_youtube}>
											<FontAwesomeIcon icon={faYoutube} />
										</a>
									)}
									{fighter.social_twitter && (
										<a href={fighter.social_twitter}>
											<FontAwesomeIcon icon={faXTwitter} />
										</a>
									)}
								</div>
							</section>
						) : null}
					</section>
				</ContentContainer>
			</section>

			<ContentContainer>
				{fighter.titles?.length > 0 && (
					<>
						<SectionHeading borderTop={false}>Career Accolades</SectionHeading>

						{fighter.current_champion && (
							<section className="champion-section">
								<div className="champion-badge">
									<img src={imgIconChampionBadgePng} alt="Champion Badge" />
								</div>
								<div className="champion-info">
									<h3 className="champion-title">Current Champion</h3>
									<p className="champion-name">{fighter.current_champion || 'WBC Heavy Weight Title holder'}</p>
								</div>
							</section>
						)}

						<div className="accolades">
							{fighter.titles?.slice(0, 5).map((t) => (
								<div className="item" key={t}>
									<div className="key">
										<img src={imgIconTrophySvg} />
									</div>
									<div className="value">{t}</div>
								</div>
							))}
						</div>
					</>
				)}

				{fighter.otx_fights?.length > 0 && (
					<section className="otx-fights">
						<SectionHeading borderTop={fighter.titles?.length > 0}>OTX Fights</SectionHeading>
						<section className="fight-cards">
							{fights.map((fight) => (
								<FightCard
									fight={fight}
									key={fight.id}
									tournamentFights={tournamentFights}
									displayOtxFightTitle={true}
									displayFightCtaButtons={true}
								/>
							))}
						</section>
					</section>
				)}
			</ContentContainer>
		</Layout>
	);
};

// Note: There can only be one export per page component for hot loading to work
export default Fighter;
